import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { UnstyledButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons/faCircleExclamation';
import { noop } from 'ui-components/default-props/noop';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';

import { ModalPortal } from '../../components/modal-portal';

import { useShipFrom } from '../../hooks/ship-froms/use-ship-from';
import { useBuildInitialShipFrom } from '../hooks/use-build-initial-ship-from';
import { useInventorySources } from '../hooks/use-inventory-sources';
import { useEnableLtlFreight } from '../../feature-flags/hooks/use-enable-ltl-freight';

import { DefaultLocationCheckbox } from '../components/default-location-checkbox';
import { Errors } from '../components/errors';
import { InventorySourceDropdown } from '../components/inventory-source-dropdown';
import { OriginAddress } from '../components/origin-address';
import { ReturnAddress } from '../components/return-address';
import { ShipFromModalFooter } from '../components/ship-from-modal-footer';
import { ShipFromModalHeader } from '../components/ship-from-modal-header';
import { ShipFromName } from '../components/ship-from-name';
import { ShipFromFreightDefaults } from '../components/ship-from-freight-defaults/ship-from-freight-defaults';
import { ShipFromContextProvider, useShipFromContext } from '../contexts/ship-from-context';

import { messages } from '../components/messages';
import style from './style.module.less';

const SECTIONS = {
  ADDRESS: 'address',
  INVENTORY_DEFAULTS: 'inventory_defaults',
  FREIGHT_DEFAULTS: 'freight_defaults',
};

const FIELD_TO_SECTION = {
  name: SECTIONS.ADDRESS,
  originAddress: SECTIONS.ADDRESS,
  returnAddress: SECTIONS.ADDRESS,
  inventorySourceId: SECTIONS.INVENTORY_DEFAULTS,
  ltlDefaults: SECTIONS.FREIGHT_DEFAULTS,
};

const getSectionsWithErrors = (validationErrors) => {
  if (!validationErrors) return [];
  const sectionSet = new Set(
    Object.keys(validationErrors)
      .map((field) => FIELD_TO_SECTION[field])
      .filter(Boolean),
  );
  return [...sectionSet];
};

const getFirstInvalidSection = (validationErrors, orderedSections) => {
  const withErrors = new Set(getSectionsWithErrors(validationErrors));
  return orderedSections.find((section) => withErrors.has(section)) ?? null;
};

const ShipFromSidebarNav = ({ sections, activeSection, onSectionChange }) => {
  const intl = useIntl();
  const { validationErrors, showValidation, saveAttemptCount } = useShipFromContext();
  const sectionsWithErrors = showValidation ? getSectionsWithErrors(validationErrors).filter((s) => s !== activeSection) : [];

  const prevSaveAttemptRef = useRef(saveAttemptCount);
  useEffect(() => {
    if (saveAttemptCount === prevSaveAttemptRef.current) return;
    prevSaveAttemptRef.current = saveAttemptCount;

    const firstInvalid = getFirstInvalidSection(validationErrors, sections);
    if (firstInvalid && firstInvalid !== activeSection) {
      onSectionChange(firstInvalid);
    }
  }, [saveAttemptCount, validationErrors, sections, activeSection, onSectionChange]);

  return (
    <nav aria-label={intl.formatMessage(messages.sidebarNavLabel)} className={style.sidebar}>
      {sections.map((section) => {
        const hasErrors = sectionsWithErrors.includes(section);
        return (
          <UnstyledButton
            className={`${style['sidebar-item']} ${activeSection === section ? style['sidebar-item--active'] : ''}`}
            key={section}
            onClick={() => onSectionChange(section)}
          >
            {intl.formatMessage(sectionMessages[section])}
            {hasErrors && (
              <FontAwesomeIcon
                aria-label={intl.formatMessage(messages.sectionHasErrors)}
                className={style['sidebar-item-error-icon']}
                icon={faCircleExclamation}
                role='img'
              />
            )}
          </UnstyledButton>
        );
      })}
    </nav>
  );
};

const sectionMessages = defineMessages({
  [SECTIONS.ADDRESS]: {
    id: 'NScYtW',
    description: 'Sidebar nav label for the Address section in Ship From modal',
    defaultMessage: 'Address',
  },
  [SECTIONS.INVENTORY_DEFAULTS]: {
    id: '/IaUlg',
    description: 'Sidebar nav label for the Inventory Defaults section in Ship From modal',
    defaultMessage: 'Inventory Defaults',
  },
  [SECTIONS.FREIGHT_DEFAULTS]: {
    id: 'QVDI0e',
    description: 'Sidebar nav label for the Freight Defaults section in Ship From modal',
    defaultMessage: 'Freight Defaults',
  },
});

/**
 * @param {object} props
 * @param {() => void} [props.onClose]
 * @param {(result: object) => void} [props.onSuccess]
 * @param {(error: object) => void} [props.onError]
 * @param {object | null} [props.prefillAddress]
 * @param {string} [props.shipFromId]
 * @param {string} [props.source]
 * @param {boolean} [props.shouldCreateInventoryWarehouse]
 * @param {string} [props.defaultSection] - Section to open by default (e.g. SECTIONS.FREIGHT_DEFAULTS).
 *   When omitted, opens to Address. Useful for deep-linking from the Booking Wizard "Edit ship from" link.
 */
export const ShipFromModal = ({
  onClose = noop,
  onSuccess = noop,
  onError = noop,
  prefillAddress = null,
  shipFromId,
  source = '',
  shouldCreateInventoryWarehouse = false,
  defaultSection = SECTIONS.ADDRESS,
}) => {
  const headerMessage = shipFromId ? messages.editShipFromModalHeader : messages.addShipFromModalHeader;
  const isLtlFreightEnabled = useEnableLtlFreight();

  const [activeSection, setActiveSection] = useState(defaultSection);
  const sidebarContentRef = useRef(null);

  const handleSectionChange = useCallback((section) => {
    setActiveSection(section);
    sidebarContentRef.current?.scrollTo({ top: 0 });
  }, []);

  const { fetch, isLoading, sources } = useInventorySources();

  useEffect(() => {
    fetch();
  }, [fetch]);

  const initialShipFrom = useBuildInitialShipFrom(sources, shouldCreateInventoryWarehouse);
  const shipFrom = useShipFrom(shipFromId) ?? initialShipFrom;

  const showInventorySection = !shouldCreateInventoryWarehouse;
  const showFreightSection = isLtlFreightEnabled && !shouldCreateInventoryWarehouse;

  const sections = [
    SECTIONS.ADDRESS,
    ...(showInventorySection ? [SECTIONS.INVENTORY_DEFAULTS] : []),
    ...(showFreightSection ? [SECTIONS.FREIGHT_DEFAULTS] : []),
  ];

  return (
    <ModalPortal onClose={onClose}>
      <Modal size='medium'>
        <ShipFromModalHeader>
          <FormattedMessage {...headerMessage} />
        </ShipFromModalHeader>
        <ShipFromContextProvider
          isInventorySourcesLoading={isLoading}
          onError={onError}
          onSuccess={onSuccess}
          prefillAddress={prefillAddress}
          shipFrom={shipFrom}
          shouldCreateInventoryWarehouse={shouldCreateInventoryWarehouse}
          source={source}
          sources={sources}
        >
          {showFreightSection ? (
            <div className={style['modal-with-sidebar']}>
              <ShipFromSidebarNav activeSection={activeSection} onSectionChange={handleSectionChange} sections={sections} />
              <div className={style['sidebar-content']} ref={sidebarContentRef}>
                <div className={style['panels-stack']}>
                  <ModalBody
                    aria-hidden={activeSection !== SECTIONS.ADDRESS}
                    className={`${style['body--sidebar']} ${activeSection !== SECTIONS.ADDRESS ? style['panel--hidden'] : ''}`}
                  >
                    <Errors />
                    <ShipFromName />
                    <DefaultLocationCheckbox />
                    <OriginAddress />
                    <ReturnAddress />
                  </ModalBody>
                  {showInventorySection && (
                    <ModalBody
                      aria-hidden={activeSection !== SECTIONS.INVENTORY_DEFAULTS}
                      className={`${style['body--sidebar']} ${activeSection !== SECTIONS.INVENTORY_DEFAULTS ? style['panel--hidden'] : ''}`}
                    >
                      <InventorySourceDropdown />
                    </ModalBody>
                  )}
                  <ModalBody
                    aria-hidden={activeSection !== SECTIONS.FREIGHT_DEFAULTS}
                    className={`${style['body--sidebar']} ${activeSection !== SECTIONS.FREIGHT_DEFAULTS ? style['panel--hidden'] : ''}`}
                  >
                    <ShipFromFreightDefaults />
                  </ModalBody>
                </div>
              </div>
            </div>
          ) : (
            <ModalBody className={style.body}>
              <Errors />
              <ShipFromName />
              <DefaultLocationCheckbox />
              <OriginAddress />
              <ReturnAddress />
              {showInventorySection && <InventorySourceDropdown />}
            </ModalBody>
          )}
          <ShipFromModalFooter onClose={onClose} />
        </ShipFromContextProvider>
      </Modal>
    </ModalPortal>
  );
};
